import isVideoFile from './isVideoFile';
import isImageFile from './isImageFile';

const CLOUDINARY_VIDEO_UPLOAD_RE =
  /^https?:\/\/res\.cloudinary\.com\/[^/]+\/video\/upload\//i;

const VIDEO_EXT_RE = /\.(mp4|m4v|webm|ogg|ogv|mov|flv|3gp|3g2|avi|mkv)$/i;

const POSTER_FIELDS = [
  'thumbnailUrl',
  'thumbnail',
  'thumbUrl',
  'posterUrl',
  'poster',
  'previewImage',
  'previewUrl',
  'imageUrl',
  'coverUrl',
];

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (value && typeof value === 'object') {
      const nested = firstText(
        value.url,
        value.secure_url,
        value.secureUrl,
        value.src,
        value.mediaUrl,
      );
      if (nested) return nested;
    }
  }
  return '';
};

const isCloudinaryVideoUploadUrl = (url) =>
  typeof url === 'string' &&
  CLOUDINARY_VIDEO_UPLOAD_RE.test(url) &&
  isVideoFile(url);

const isVersionSegment = (segment) => /^v\d+$/.test(String(segment || ''));

const looksLikeTransformSegment = (segment) => {
  const value = String(segment || '');
  if (!value) return false;
  if (value.includes(',')) return true;
  return /^(a|ar|b|bo|br|c|co|cs|d|dl|dpr|e|f|fl|fn|g|h|l|o|pg|q|r|so|t|u|vc|w|x|y|z)_/.test(
    value,
  );
};

const splitUploadUrl = (url) => {
  if (!url || typeof url !== 'string' || !url.includes('/upload/')) return null;
  const [left, rightRaw] = url.split('/upload/');
  const suffixMatch = String(rightRaw || '').match(/^([^?#]*)([?#].*)?$/);
  const right = (suffixMatch?.[1] || '').replace(/\/+$/, '');
  const suffix = suffixMatch?.[2] || '';
  let tailParts = right.split('/').filter(Boolean);

  let forcedTxSegs = null;
  if (tailParts.length === 1 && /,v\d+/.test(tailParts[0])) {
    const commaParts = tailParts[0]
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const vIndex = commaParts.findIndex((p) => isVersionSegment(p));
    if (vIndex !== -1) {
      forcedTxSegs = commaParts.slice(0, vIndex);
      tailParts = commaParts.slice(vIndex);
    }
  }

  const versionIndex = tailParts.findIndex((part) => isVersionSegment(part));
  let txParts = [];
  let assetParts = tailParts;

  if (forcedTxSegs) {
    txParts = [...forcedTxSegs];
    assetParts = tailParts;
  } else if (versionIndex === 0) {
    txParts = [];
    assetParts = tailParts;
  } else if (versionIndex > 0) {
    txParts = tailParts.slice(0, versionIndex);
    assetParts = tailParts.slice(versionIndex);
  } else {
    const firstAssetIndex = tailParts.findIndex(
      (part) => !looksLikeTransformSegment(part),
    );
    if (firstAssetIndex > 0) {
      txParts = tailParts.slice(0, firstAssetIndex);
      assetParts = tailParts.slice(firstAssetIndex);
    } else {
      txParts = looksLikeTransformSegment(tailParts[0])
        ? [tailParts[0]]
        : [];
      assetParts = txParts.length ? tailParts.slice(1) : tailParts;
    }
  }

  return { left, txParts, assetParts, suffix };
};

const normalizeTransforms = (txParts) =>
  Array.from(
    new Set(
      txParts
        .join(',')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    ),
  );

const replaceVideoExtensionWithJpg = (filename) =>
  String(filename || '').replace(VIDEO_EXT_RE, '.jpg');

const getExplicitThumbnailUrl = (entry) => {
  if (!entry || typeof entry !== 'object') return '';
  const candidate = firstText(...POSTER_FIELDS.map((field) => entry[field]));
  if (!candidate || isVideoFile(candidate)) return '';
  return candidate;
};

export const normalizeMediaList = (item) => {
  if (!item) return [];
  const buildEntry = (entry) => {
    if (!entry) return null;
    if (typeof entry === 'string') {
      return { url: entry, type: isVideoFile(entry) ? 'video' : 'image' };
    }
    const url =
      entry.url ||
      entry.secure_url ||
      entry.secureUrl ||
      entry.preview ||
      entry.src ||
      entry.mediaUrl ||
      null;
    if (!url) return null;
    const type = entry.type || (isVideoFile(url) ? 'video' : 'image');
    const thumbnailUrl = getExplicitThumbnailUrl(entry);
    return {
      url,
      type,
      ...(thumbnailUrl ? { thumbnailUrl } : {}),
    };
  };

  if (Array.isArray(item.media) && item.media.length) {
    return item.media.map(buildEntry).filter(Boolean);
  }
  if (Array.isArray(item.proofMedia) && item.proofMedia.length) {
    return item.proofMedia.map(buildEntry).filter(Boolean);
  }
  if (Array.isArray(item.images) && item.images.length) {
    return item.images.map(buildEntry).filter(Boolean);
  }
  if (item.img) {
    return [
      { url: item.img, type: isVideoFile(item.img) ? 'video' : 'image' },
    ];
  }
  if (item.coverMedia) {
    return [buildEntry(item.coverMedia)].filter(Boolean);
  }
  return [];
};

export const buildNormalizedUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('/upload/')) return url;

  const parsed = splitUploadUrl(url);
  if (!parsed?.assetParts?.length) return url;
  let tx = normalizeTransforms(parsed.txParts);

  const video = isVideoFile(url);
  const image = !video && isImageFile(url);

  if (video) {
    tx = tx.filter((t) => !t.startsWith('fl_progressive'));
    const required = ['f_auto', 'q_auto', 'vc_auto'];
    required.forEach((r) => {
      if (!tx.includes(r)) tx.push(r);
    });
  } else if (image) {
    if (!tx.some((t) => t.startsWith('q_auto'))) tx.unshift('q_auto');
    if (!tx.find((t) => t.startsWith('fl_progressive:steep'))) {
      tx.push('fl_progressive:steep');
    }
  }

  const finalTx = tx.join(',');
  const pathTail = parsed.assetParts.join('/');
  if (!pathTail) return url;
  if (!finalTx) return `${parsed.left}/upload/${pathTail}${parsed.suffix}`;
  return `${parsed.left}/upload/${finalTx}/${pathTail}${parsed.suffix}`;
};

export const derivePoster = (url) => {
  if (!url || !isVideoFile(url)) return '';
  if (!isCloudinaryVideoUploadUrl(url)) return '';
  const parsed = splitUploadUrl(url);
  if (!parsed?.assetParts?.length) return '';
  const assetParts = [...parsed.assetParts];
  const last = assetParts[assetParts.length - 1];
  if (!VIDEO_EXT_RE.test(last)) return '';
  assetParts[assetParts.length - 1] = replaceVideoExtensionWithJpg(last);

  const existingTx = normalizeTransforms(parsed.txParts).filter(
    (t) =>
      t !== 'f_auto' &&
      t !== 'vc_auto' &&
      !t.startsWith('vc_') &&
      !t.startsWith('f_mp4') &&
      !t.startsWith('f_webm') &&
      !t.startsWith('fl_progressive'),
  );
  const tx = ['f_jpg', 'q_auto', 'so_1'];
  existingTx.forEach((t) => {
    if (
      !tx.includes(t) &&
      t !== 'q_auto' &&
      !t.startsWith('q_auto') &&
      !t.startsWith('so_') &&
      !t.startsWith('f_')
    ) {
      tx.push(t);
    }
  });

  return `${parsed.left}/upload/${tx.join(',')}/${assetParts.join('/')}${
    parsed.suffix
  }`;
};

export const getMediaThumbnailUrl = (media) => {
  if (!media?.url) return null;
  if (media.type === 'video' || isVideoFile(media.url)) {
    return getExplicitThumbnailUrl(media) || derivePoster(media.url) || null;
  }
  if (isVideoFile(media.url)) return null;
  return buildNormalizedUrl(media.url);
};

const resolveCoverIndex = (item, fallbackIndex) => {
  if (!item) return fallbackIndex ?? 0;
  if (Number.isInteger(item.coverIndex)) return item.coverIndex;
  if (Number.isInteger(fallbackIndex)) return fallbackIndex;
  return 0;
};

export const getCoverMedia = (item, coverIndex) => {
  const list = normalizeMediaList(item);
  if (!list.length) return null;
  const idx = resolveCoverIndex(item, coverIndex);
  return list[idx] || list[0] || null;
};

export const getMediaThumbnail = (item, coverIndex) => {
  const cover = getCoverMedia(item, coverIndex);
  return getMediaThumbnailUrl(cover);
};
